import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { animated } from 'react-spring';
import ReactTooltip from 'react-tooltip';
import { useCallback } from 'react';

import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getSimilaritiesFilterById,
  getSimilaritiesPersonId,
  getSimilaritiesSettings,
} from 'SimilaritiesPackage/MainModule/selectors/similaritiesSelectors';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import {
  similaritiesChangeStatus,
  similaritiesExportById,
} from 'SimilaritiesPackage/MainModule/actions/similaritiesActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ISimilaritiesMatchByPersonFilter } from 'APIPackage/SimilarityModule/interfaces/ISimilaritiesMatchByPersonFilter';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { ISimilarityStatusEnum } from 'APIPackage/SimilarityModule/interfaces/ISimilarityStatus';
import { compareDates, formatDate, getFormatedClientTime } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { addNoticeImportNotification } from 'CorePackage/NotificationModule/common/addNoticeImportNotification';

import styles from './SimilaritiesByPersonDropdown.module.scss';

interface ISimilaritiesByPersonProps {
  isSupervisor: boolean;
  filter: ISimilaritiesMatchByPersonFilter;
  personId: string | undefined;
  activeIds: string[] | number[];
  settings: IAdminSetting | undefined;
  onMergeClick: () => void;
}

const SimilaritiesByPersonDropdownWithoutStore = ({
  isSupervisor,
  filter,
  personId,
  activeIds,
  settings,
  onMergeClick,
}: ISimilaritiesByPersonProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const compareSettingsAndCurrentDate = compareDates({
    dateLeft: getFormatedClientTime(SYSTEM_DATE_FORMAT_FNS),
    dateRight: settings?.value || '',
  });

  const isExportForbidden =
    typeof compareSettingsAndCurrentDate === 'number' ? compareSettingsAndCurrentDate === -1 : false;

  const exportNotify = ({ id }: IExport) => {
    dispatch(addNoticeImportNotification(id));
  };

  const handleExport = (format: ExportFormatsEnum, id: string | undefined) => () =>
    dispatch(similaritiesExportById(format, id ?? '', filter, exportNotify));

  const actionItemsSupervisor = [
    {
      id: 'action-export-xlsx',
      content: <DropdownItem onClick={handleExport(ExportFormatsEnum.XLSX, personId)}>Экспорт в XLSX</DropdownItem>,
    },
    {
      id: 'action-export-ods',
      content: <DropdownItem onClick={handleExport(ExportFormatsEnum.ODS, personId)}>Экспорт в ODS</DropdownItem>,
    },
  ];

  const onResolveClick = useCallback(() => {
    dispatch(
      similaritiesChangeStatus(ISimilarityStatusEnum.RESOLVED, String(personId), '', { knmIds: activeIds }, true),
    );
  }, [activeIds, dispatch, personId]);

  const actionItems = [
    {
      id: 'approve',
      content: (
        <div className={styles.ButtonWrapper}>
          <DropdownItem onClick={onResolveClick} isDisabled={activeIds.length === 0 || isExportForbidden}>
            Принудительно разрешить
          </DropdownItem>
          {isExportForbidden && settings?.value && (
            <>
              <animated.button data-tip="React-tooltip" type="button" className={styles.ButtonUp}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23">
                  <circle fill="#e9edf2" cx="11.5" cy="11.5" r="11.5" />
                  <path
                    fill="#4f6070"
                    d="M10.7,15.5l1-6.8h1.2l-1,6.8H10.7z M13.2,5.6c0.1,0.1,0.2,0.3,0.2,0.5c0,0.3-0.1,0.5-0.2,0.6
    C13,6.9,12.8,7,12.5,7c-0.2,0-0.4-0.1-0.5-0.2c-0.1-0.1-0.2-0.3-0.2-0.5c0-0.3,0.1-0.5,0.3-0.6c0.2-0.2,0.4-0.2,0.6-0.2
    C12.9,5.4,13.1,5.4,13.2,5.6z"
                  />
                </svg>
              </animated.button>
              <ReactTooltip className={styles.Tooltip} place="left" type="dark" effect="solid">
                <span>
                  Экспорт возможен с&nbsp;
                  {formatDate({ date: settings?.value || '', dateFormat: DATE_FORMAT_LONG_FNS })}
                </span>
              </ReactTooltip>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'merge',
      content: (
        <div className={styles.ButtonWrapper}>
          <DropdownItem onClick={onMergeClick} isDisabled={activeIds.length === 0 || isExportForbidden}>
            Совместить
          </DropdownItem>
          {isExportForbidden && settings?.value && (
            <>
              <animated.button data-tip="React-tooltip" type="button" className={styles.ButtonUp}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23">
                  <circle fill="#e9edf2" cx="11.5" cy="11.5" r="11.5" />
                  <path
                    fill="#4f6070"
                    d="M10.7,15.5l1-6.8h1.2l-1,6.8H10.7z M13.2,5.6c0.1,0.1,0.2,0.3,0.2,0.5c0,0.3-0.1,0.5-0.2,0.6
    C13,6.9,12.8,7,12.5,7c-0.2,0-0.4-0.1-0.5-0.2c-0.1-0.1-0.2-0.3-0.2-0.5c0-0.3,0.1-0.5,0.3-0.6c0.2-0.2,0.4-0.2,0.6-0.2
    C12.9,5.4,13.1,5.4,13.2,5.6z"
                  />
                </svg>
              </animated.button>
              <ReactTooltip className={styles.Tooltip} place="left" type="dark" effect="solid">
                <span>
                  Экспорт возможен с&nbsp;
                  {formatDate({ date: settings?.value || '', dateFormat: DATE_FORMAT_LONG_FNS })}
                </span>
              </ReactTooltip>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'divider-1',
      isDivider: true,
    },
    {
      id: 'action-export-xlsx',
      content: <DropdownItem onClick={handleExport(ExportFormatsEnum.XLSX, personId)}>Экспорт в XLSX</DropdownItem>,
    },
    {
      id: 'action-export-ods',
      content: <DropdownItem onClick={handleExport(ExportFormatsEnum.ODS, personId)}>Экспорт в ODS</DropdownItem>,
    },
  ];

  return <Dropdown items={isSupervisor ? actionItemsSupervisor : actionItems} />;
};

const mapStateToProps = (state: IRootState) => ({
  filter: getSimilaritiesFilterById(state),
  personId: getSimilaritiesPersonId(state),
  settings: getSimilaritiesSettings(state),
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const SimilaritiesByPersonDropdown = connect(mapStateToProps)(SimilaritiesByPersonDropdownWithoutStore);
